import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /*
   * Sleeplay divergence from upstream Horizon (2026-11-03) — keep these three when pulling an update.
   *
   * The scroll lock below pins <body> at `position: fixed`, and upstream only ever undoes that in
   * closeDialog(). Three ways it outlived the dialog, or doubled up:
   *   1. The dialog closes without closeDialog(): `dialog.close()` from other code, or a platform
   *      close (the Android back gesture). The page stayed pinned and unscrollable, and nothing heard
   *      `dialog:close` — so the flows waiting on it (the cart after the RX popup, the RX popup after
   *      quick-add) never ran. Now the native `close` event releases the lock and announces it.
   *   2. showModal() throws inside the frame callback (dialog detached, already open elsewhere).
   *      The lock was already on and the outer try cannot see the error. Now it is released.
   *   3. A second open lands before the first frame. Measured live 2026-11-03: after a bundle swap
   *      Rebuy fires `rebuy.hide` twice plus `rebuy.add`, and two handlers each open the drawer —
   *      `show` and `dialog:open` ran twice. Now the second request is a no-op.
   * Background: docs/reports/POSTHOG_FEEDBACK_CART_2026-11.md.
   */

  /** @type {boolean} Whether this dialog currently holds the body scroll lock. */
  #holdsScrollLock = false;

  /** @type {boolean} Whether an open is already scheduled for the next frame. */
  #openScheduled = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    try {
      const { dialog } = this.refs;

      if (dialog.open || this.#openScheduled) return;

      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;
      this.#openScheduled = true;

      // Prevent layout thrashing by separating DOM reads from DOM writes
      requestAnimationFrame(() => {
        this.#openScheduled = false;

        try {
          document.body.style.width = '100%';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          this.#holdsScrollLock = true;

          if (dialog.classList.contains('js-cart-drawer-dialog-no-modal')) {
            dialog.show();
          } else {
            dialog.showModal();
          }

          dialog.addEventListener('close', this.#handleNativeClose, { once: true });

          this.dispatchEvent(new DialogOpenEvent());

          this.addEventListener('click', this.#handleClick);
          this.addEventListener('keydown', this.#handleKeyDown);
        } catch (error) {
          this.#releaseScrollLock();
          console.error('DialogComponent.showDialog failed:', error);
        }
      });
    } catch (error) {
      this.#openScheduled = false;
      console.error('DialogComponent.showDialog failed:', error);
    }
  }

  /** Unpins <body> and puts the page back where it was, if this dialog pinned it. */
  #releaseScrollLock() {
    if (!this.#holdsScrollLock) return;
    this.#holdsScrollLock = false;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * The dialog closed without going through closeDialog(). closeDialog() releases the lock before
   * it calls `dialog.close()`, so on that path this finds nothing to do.
   */
  #handleNativeClose = () => {
    if (!this.#holdsScrollLock) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.refs.dialog?.classList.remove('dialog-closing');
    this.#releaseScrollLock();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    try {
      const { dialog } = this.refs;

      if (!dialog.open) return;

      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      this.#releaseScrollLock();

      dialog.close();
      dialog.classList.remove('dialog-closing');

      this.dispatchEvent(new DialogCloseEvent());
    } catch (error) {
      console.error('DialogComponent.closeDialog failed:', error);
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (this.hasAttribute('data-prevent-outside-click')) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
